import { HidingError } from "components/errors";
import styles from "./ReportPage.module.css";
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from "@mui/x-tree-view";
import { Button, Divider, FormControl, FormControlLabel, FormGroup, Paper, Stack, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { setExportType, setSelected } from "store/report/reportSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReportParams from "./ReportParams";
import { useGetProfilesQuery } from "services/data/profileApi";
import { useGetAppsQuery } from "services/data/appApi";
import { Print, Refresh, SaveAlt } from "@mui/icons-material";
import { toIsoString } from "utils/date";
import { parse } from "date-fns";
import { useGetReportTypesQuery, useLazyGetReportQuery } from "services/data/reportApi";
import { CircularProgress } from "components/overlays";
import ReportBody from "./ReportBody";
import { gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid';
import { reportTypes } from "./ReportBody/Types";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import {robotoNormal, robotoBold} from "assets/fonts/roboto-base64";
import { getDefaultQuerySearch } from "store/searchbar/selectors";
import { useQueryStale } from "rtkq/useQueryStale";

const dateFormat = "dd MMMM yyyy";
const referenceDate = new Date(1970, 0, 1, 0, 0, 0);
const DEFAULT_ERROR = "Ошибка соединения с сервером";

const buildTree = (map) => {
  const result = [];
  const nodeMap = new Map();

  map.forEach((node, id) => nodeMap.set(id, { ...node, id: id, children: [] }));
  nodeMap.forEach(node => {
    if(node.parentId) {
      const parent = nodeMap.get(node.parentId);
      if(parent) {
        parent.children.push(node);
      }
    }
    else {
      result.push(node);
    }
  });

  return result;
};

const renderTree = (nodes) => nodes.map(node => (
    <TreeItem key={node.id} itemId={node.id} label={node.label} disableSelection={node.children?.length > 0} className={(node.children?.length > 0) ? styles.rootElement : ''}>
      {node.children?.length > 0 && renderTree(node.children)}
    </TreeItem>
  )
);



const ReportPage = ({setActiveToolbar, defaultSearch}) => {

  useEffect( () => setActiveToolbar('report'), []);

  const profsQueryArgs = getDefaultQuerySearch(defaultSearch.profile);
  const appsQueryArgs = getDefaultQuerySearch(defaultSearch.app); 

  const { selected, exportType, params } = useSelector(state => state.report);
  const dispatch = useDispatch();

  const profileQuery = useGetProfilesQuery(profsQueryArgs);
  const { data: profilesData, isError: isGetProfilesError } = profileQuery;
  useQueryStale({query: profileQuery, endpointName: 'getProfiles', queryArgs: profsQueryArgs});
  
  const appQuery = useGetAppsQuery(appsQueryArgs);
  const { data: appsData, isError: isGetAppsError } = appQuery;
  useQueryStale({query: appQuery, endpointName: 'getApps', queryArgs: appsQueryArgs});


  const { data: reportTypesData, isError: isGetReportTypesError } = useGetReportTypesQuery();
  const [ trigger, { data: reportData, isError: isGetReportError, error: reportError, isFetching: isFetchingReport}] = useLazyGetReportQuery();
  const [showReport, setShowReport] = useState(false);
  const [showError, setShowError] = useState(false);
  const [visibledColumns, setVisibledColumns] = useState({});
  const gridRef = useRef(null);

  const typesData = useMemo(() => {
    return new Map(reportTypesData?.body.items.map( item => [item.id, item.value]));
  }, [reportTypesData]);

  useEffect(() => {
    isGetReportError && setShowError(true);
  }, [isGetReportError]);
    
  /*const openError = () => {  
    return isGetReportError || false;
  };*/

  const getErrorMessage = () => { 
    if(isGetReportError) return reportError?.data?.message ?? DEFAULT_ERROR;
    else return 'Ошибка';
  };

  const hideError = () => setShowError(false);

  const validCombo = (item) => !!params[item.name];
  const validDaterange = (item) => {
    const startAt = params[item.nameStart] ? parse(params[item.nameStart], dateFormat, referenceDate) : (new Date(0));
    const endAt = params[item.nameEnd] ? parse(params[item.nameEnd], dateFormat, referenceDate) : (new Date());
    return endAt >= startAt;
  };

  const validateParam = (item) => {
    switch(item.type) {
      case 'combo': return validCombo(item);
      case 'daterange': return validDaterange(item);
      default: return false;
    }
  }

  const isValid = useMemo(() => {
    setShowReport(false);
    return selected.params?.every(validateParam);
  }, [params, selected]);

  const reportParams = useMemo(() => {
    const result = selected.params?.map(item => {
        switch(item.type) {
          case 'combo': return {[item.name]: params[item.name]};
          case 'daterange': {
            const startAt = params[item.nameStart] ? parse(params[item.nameStart], dateFormat, referenceDate) : (new Date(0));
            const endAt = params[item.nameEnd] ? parse(params[item.nameEnd], dateFormat, referenceDate) : (new Date());
            return {[item.nameStart]: toIsoString(startAt), [item.nameEnd]: toIsoString(endAt)};
          }
        }
      })
      .flatMap(obj => Object.entries(obj).map(([key, value]) => ({[key]: value})))
      .reduce((acc, obj) => ({ ...acc, ...obj }), {});
    
    result && (result.id = selected.id);
    return result;
      
    
  }, [params, selected]);

  useEffect(() => {
    if(selected?.id) {
      const result = reportTypes[selected.id]?.map(el => el.field) ?? []
      setVisibledColumns(result.reduce((acc, field) => ({...acc, [field]: true}), {}));
    }
  }, [selected]);


  const handleChangeVisible = (e) => {
    const { value, checked } = e.target;
    setVisibledColumns(prev => ({...prev, [value]: checked}));
  };

  const exportEnabled = useMemo(() => {
    return reportData && showReport && Object.values(visibledColumns).some(visible => visible === true);
  }, [reportData, visibledColumns, showReport]);

  const columnsSelector = useMemo(() => {   

    const items = reportTypes[selected?.id]?.filter(item => item.field !== 'actions').map(item => {
      return <FormControlLabel 
        key={item.field}
        control={<Switch checked={!!visibledColumns[item.field]} onChange={handleChangeVisible} size="small" value={item.field} />} 
        label={item.headerName}
        labelPlacement="start"
        slotProps={{
          typography: {
            variant: 'body2'
          }
        }} 
        sx={{justifyContent: 'space-between', margin: '4px 0'}}
      />
    });
    return (
      <FormGroup sx={{marginTop: 2}}>
        <Typography variant="subtitle2" my={1} sx={{ textAlign: 'center'}}>Данные для экспорта</Typography>
        {items}
      </FormGroup>
    );
  }, [selected, gridRef, visibledColumns]);

  const makeExcel = (fname) => {
    const api = gridRef.current?.current;
    api.exportDataAsCsv({
      fileName: fname,
      utf8WithBom: true,
      includeHeaders: true,
      delimiter: ',',
      fields: Object.entries(visibledColumns)
                .filter(([field, visible]) => visible)
                .filter(([field, visible]) => field !== 'actions')
                .map(([field]) => field)
    });
  };

  const makePdf = (fname) => {
    const api = gridRef.current;
    const fields = Object.entries(visibledColumns).filter(([field, visible]) => visible).map(([field]) => field);
    const visibledHeaders = reportTypes[selected?.id]?.filter(item => fields.includes(item.field));
    const rowIds = gridFilteredSortedRowIdsSelector(api);
    const rows = rowIds.map(id => api?.current.getRow(id));
    const doc = new jsPDF();

    doc.addFileToVFS('Roboto.ttf', robotoNormal);
    doc.addFileToVFS('Roboto-Bold.ttf', robotoBold);
    doc.addFont('Roboto.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    doc.setFont('Roboto');

    doc.text(fname, 10, 10);
    autoTable(doc, {
      head: [visibledHeaders.map(item => item.headerName)],
      body: rows.map(row => visibledHeaders.map(item => api?.current.getCellParams(row.virtualId, item.field).formattedValue)),
      startY: 20,
      styles: { font: 'Roboto', fontSize: 8, overflow: 'linebreak' },
      headStyles: { font: 'Roboto'},
      bodyStyles: { font: 'Roboto' } 
    });
    doc.save(`${fname}.pdf`);
  };
  

  const makeExport = () => {
    switch(exportType) {
      case 'excel': return makeExcel(exportFileName);
      case 'acrobat': return makePdf(exportFileName);
    }
  };

  const makeReport = async () => {
    setShowReport(false)
    trigger(reportParams) 
      .unwrap()
      .then(() => setShowReport(true))
      .catch(() => setShowReport(false));

    
  };

  const renderTitle = useCallback((data) => {
    if(selected?.id) {
      const parent = data.get(selected.parentId);
      return parent && (`${parent.label} -> ${selected.label}`);
    }
    return '<- Выберите отчёт из списка'
  }, [selected]);
  
  const exportFileName = useMemo(() => {
    if(selected?.id) {
      const parent = typesData.get(selected.parentId);
      return parent && (`${parent.label} - ${selected.label} от ${new Date().toLocaleDateString()}`);
    }
  }, [selected, typesData]);

  const onSelectReport = (id) => {
    dispatch(setSelected({ id, ...typesData.get(id) }));
  };

  return (
    <div className="page">
      <HidingError 
        open={ showError }
        message={ getErrorMessage() }
        onClose={ hideError }
      />
      <div className={styles.overlayWrapper}>
        <div className={styles.report}>
          <Paper className={styles.treeWrapper}>
              <SimpleTreeView 
                className={styles.tree}
                defaultExpandedItems={['device', 'app', 'profile', 'lic', 'kabinet']}
                selectedItems={selected?.id ?? ''}
                onSelectedItemsChange={(_e, id) => onSelectReport(id)} 
                disabledItemsFocusable={false}
              >
                {renderTree(buildTree(typesData ?? []))}
              </SimpleTreeView>
          </Paper>    
          <Paper className={styles.reportWrapper}>
            <Stack direction="row" sx={{width: '100%', height: '100%'}}>
              <Stack sx={{alignItems: 'center', flex: '1', minWidth: 0, padding: 2}}>
                <Typography variant="h5" mt={1} mb={2} sx={{fontSize: '1.3rem'}}>{renderTitle(typesData)}</Typography>
                <ReportParams params={selected?.params ?? []} apps={appsData?.body.items} profiles={profilesData?.body.items}/>
                <Stack className={styles.reportBodyWrapper}>
                  <CircularProgress open={isFetchingReport} />
                  { (reportData && showReport) ? <ReportBody type={selected?.id} data={reportData} setVisibledColumns={setVisibledColumns} onReady={api => gridRef.current = api} /> : <div className={styles.noData}>Здесь пока ничего нет</div> }
                </Stack>
              </Stack>
              
              <Stack spacing={1} className={styles.tools}>
                <Button
                  fullWidth
                  color='error'
                  startIcon={<Refresh/>}
                  onClick={makeReport}
                  disabled={!isValid}
                >
                  Построить
                </Button>
                <Button
                  fullWidth
                  startIcon={<SaveAlt/>}
                  disabled={!exportEnabled}
                  onClick={makeExport}
                >
                  Экспорт
                </Button>
                <ToggleButtonGroup 
                  color="primary"
                  size="small"
                  exclusive
                  value={exportType}
                  disabled={!exportEnabled}
                  onChange={(e, value) => value && dispatch(setExportType(value))}
                  sx={{justifyContent: 'center'}}
                >
                  <ToggleButton value="excel">CSV</ToggleButton>
                  <ToggleButton value="acrobat">PDF</ToggleButton>
                </ToggleButtonGroup>
                { (reportData && showReport) ? columnsSelector : null}
              </Stack>
            </Stack>
            
          </Paper>
        </div>
      </div> 
    </div>
  );
};

export default ReportPage;